/**
 * In-app history position, so gestures know whether "back" and "forward" have
 * somewhere to go. React Router's BrowserRouter stamps every entry it creates
 * with `history.state.idx` (0 for the first entry of this document); we track
 * the highest index still reachable going forward. A PUSH truncates the
 * forward entries, so max becomes the new index; POP and REPLACE keep it.
 *
 * The max survives reloads (sessionStorage) but not a fresh navigation to the
 * site, which starts a new forward-less list.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const KEY = 'nav.maxIdx';

export interface NavPos {
  idx: number;
  max: number;
}

let pos: NavPos = { idx: 0, max: 0 };
const ls = new Set<() => void>();

const readIdx = (): number => {
  const s = window.history.state as { idx?: unknown } | null;
  return typeof s?.idx === 'number' ? s.idx : 0;
};

function storedMax(): number | null {
  try {
    const nav = performance.getEntriesByType?.('navigation')[0] as PerformanceNavigationTiming | undefined;
    // A fresh load starts a new list with nothing ahead; a reload or back/forward into the page keeps it.
    if (nav && nav.type === 'navigate') return null;
    const v = Number(sessionStorage.getItem(KEY));
    return Number.isFinite(v) ? v : null;
  } catch {
    return null;
  }
}

function set(next: NavPos): void {
  if (next.idx === pos.idx && next.max === pos.max) return;
  pos = next;
  try {
    sessionStorage.setItem(KEY, String(next.max));
  } catch {
    /* private mode */
  }
  ls.forEach((fn) => fn());
}

/** Pure transition, exported for tests. */
export function nextPos(prev: NavPos, idx: number, type: 'PUSH' | 'POP' | 'REPLACE'): NavPos {
  if (type === 'PUSH') return { idx, max: idx };
  return { idx, max: Math.max(prev.max, idx) };
}

let initialised = false;

/** Call once inside the router (Shell). */
export function useTrackHistory(): void {
  const loc = useLocation();
  const type = useNavigationType();
  useEffect(() => {
    const idx = readIdx();
    if (!initialised) {
      initialised = true;
      set({ idx, max: Math.max(idx, storedMax() ?? idx) });
      return;
    }
    set(nextPos(pos, idx, type));
  }, [loc.key, type]);
}

export const navPos = (): NavPos => pos;
export const canGoBack = (): boolean => pos.idx > 0;
export const canGoForward = (): boolean => pos.idx < pos.max;

export function useNavPos(): NavPos {
  return useSyncExternalStore(
    (fn) => {
      ls.add(fn);
      return () => ls.delete(fn);
    },
    () => pos,
    () => pos,
  );
}
